/**
 * Invoices and receipts, wherever they are needed.
 *
 * This list lived only on the billing page, one click further in than the
 * subscription page a traveller actually opens to ask "what am I paying, and
 * what have I paid". It is a component now so both places show the same rows
 * rather than one of them linking to the other.
 *
 * Nothing is stored on our side. Each row is read from Stripe when it mounts
 * and the PDF link is Stripe's own: the invoice of record is the one the
 * payment processor issued, and a copy of ours could disagree with it after a
 * refund or a correction.
 */

import { BillingDocument, fetchBillingDocuments } from "@/services/stripe";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useAuthStore } from "@/stores/authStore";
import { Download, ExternalLink, Receipt } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

function formatAmount(
  amount: number,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

const STATUS_STYLE: Record<string, string> = {
  paid: "bg-green-50 text-green-700 border-green-200",
  open: "bg-amber-50 text-amber-700 border-amber-200",
  uncollectible: "bg-red-50 text-red-700 border-red-200",
  void: "bg-gray-100 text-gray-600 border-gray-200",
  refunded: "bg-gray-100 text-gray-600 border-gray-200",
  draft: "bg-gray-100 text-gray-600 border-gray-200",
};

export function BillingDocuments() {
  const { token } = useAuthStore();
  const fmt = useLocaleFormat();
  const [documents, setDocuments] = useState<BillingDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      setDocuments(await fetchBillingDocuments(token));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Your invoices could not be loaded right now.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
        <Receipt className="h-5 w-5 text-gray-500" />
        <h2 className="text-lg font-semibold text-gray-900">
          Invoices and payments
        </h2>
      </div>

      {loading ? (
        <div className="space-y-3 p-6">
          <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
          <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
        </div>
      ) : error ? (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={loadDocuments}
            className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            Try again
          </button>
        </div>
      ) : documents.length === 0 ? (
        <p className="px-6 py-8 text-center text-sm text-gray-500">
          Nothing has been charged yet. Invoices appear here the moment a
          payment goes through.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 px-6 py-4"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900">
                  {doc.number ? `Invoice ${doc.number}` : "Payment"}
                  <span
                    className={`ml-2 rounded-full border px-2 py-0.5 text-xs font-medium ${
                      STATUS_STYLE[doc.status] ??
                      "bg-gray-100 text-gray-600 border-gray-200"
                    }`}
                  >
                    {doc.status}
                  </span>
                </p>
                <p className="truncate text-sm text-gray-500">
                  {fmt.date(doc.created, "medium")}
                  {doc.description ? ` · ${doc.description}` : ""}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <span className="font-semibold tabular-nums text-gray-900">
                  {formatAmount(doc.amount, doc.currency, fmt.locale)}
                </span>
                {doc.pdfUrl ? (
                  <a
                    href={doc.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4" />
                    PDF
                  </a>
                ) : doc.hostedUrl ? (
                  <a
                    href={doc.hostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Receipt
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
